import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { exportNotes, getNote, getNotes, onNotesChange, setNote } from "@/lib/notes";
import { SITES } from "@/data/sites";

const MAX = 1200;

export default function FieldNotes({ siteId }) {
  const [text, setText] = useState(() => getNote(siteId));
  const [state, setState] = useState("idle");
  const [noteCount, setNoteCount] = useState(() => Object.keys(getNotes()).length);
  const timer = useRef(null);

  useEffect(() => onNotesChange((all) => setNoteCount(Object.keys(all).length)), []);

  useEffect(() => {
    setText(getNote(siteId));
    setState("idle");
  }, [siteId]);

  const onChange = (e) => {
    const v = e.target.value.slice(0, MAX);
    setText(v);
    setState("saving");
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setNote(siteId, v);
      setState("saved");
    }, 500);
  };

  useEffect(() => () => clearTimeout(timer.current), []);

  return (
    <div data-testid="field-notes" className="mt-9">
      <div className="flex items-baseline justify-between">
        <p className="font-mono text-[10px] tracking-[0.35em] text-[#D4AF37]">FIELD NOTES</p>
        <span data-testid="field-notes-status" className="font-mono text-[8px] tracking-[0.25em] text-white/35">
          {state === "saving" ? "SAVING…" : state === "saved" ? "SAVED ON THIS DEVICE" : "PRIVATE · THIS DEVICE ONLY"}
        </span>
      </div>
      <textarea
        data-testid="field-notes-input"
        value={text}
        onChange={onChange}
        rows={4}
        placeholder="What do you make of it? Your notes never leave this browser."
        className="mt-3 w-full resize-y border border-white/15 bg-[#0B0C10]/60 p-4 font-serif text-base leading-relaxed text-[#F8FAFC] placeholder:text-white/25 focus:border-[#D4AF37]/60 focus:outline-none"
      />
      <div className="mt-1.5 flex justify-between font-mono text-[8px] tracking-[0.2em] text-white/30">
        <span>{text.length ? `${text.length}/${MAX}` : ""}</span>
        <span className="flex gap-4">
          {noteCount > 0 && (
            <button
              data-testid="field-notes-export"
              onClick={() => {
                const n = exportNotes(SITES);
                if (n) toast.success(`Exported ${n} field note${n === 1 ? "" : "s"} as a text file`);
              }}
              className="text-[#D4AF37]/80 transition-colors hover:text-[#D4AF37]"
            >
              ↓ EXPORT ALL NOTES ({noteCount})
            </button>
          )}
          {text.length > 0 && (
            <button
              data-testid="field-notes-clear"
              onClick={() => {
                clearTimeout(timer.current);
                setText("");
                setNote(siteId, "");
                setState("idle");
              }}
              className="transition-colors hover:text-[#FF9F1C]"
            >
              CLEAR NOTE
            </button>
          )}
        </span>
      </div>
    </div>
  );
}
